import * as tf from '@tensorflow/tfjs';
import { ACOUSTIC_DEFAULTS } from '../config';
import { mean } from '../utils';
import { YAMNET_GROUP_CLASS_INDICES, type YAMNetGroupKey } from './yamnetClassMap';

const YAMNET_MODEL_URL = '/models/yamnet/model.json';

export type GroupedYamnetScores = Record<YAMNetGroupKey, number>;

const EMPTY_GROUPED_SCORES: GroupedYamnetScores = {
  speech: 0,
  conversation: 0,
  babble: 0,
  shouting: 0
};

function normalizeScore(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function aggregateGroup(frameScores: number[], indices: number[]): number {
  if (!indices.length) return 0;
  const values = indices
    .filter((index) => index >= 0 && index < frameScores.length)
    .map((index) => frameScores[index] ?? 0);
  if (!values.length) return 0;
  const maxValue = Math.max(...values);
  const meanValue = mean(values);
  return normalizeScore(maxValue * 0.65 + meanValue * 0.35);
}

function resizeToWindow(samples: Float32Array, targetLength: number): Float32Array {
  if (samples.length === targetLength) return samples;
  if (!samples.length) return new Float32Array(targetLength);
  if (samples.length > targetLength) {
    const start = Math.max(0, Math.floor((samples.length - targetLength) / 2));
    return samples.slice(start, start + targetLength);
  }

  const resized = new Float32Array(targetLength);
  const maxIndex = samples.length - 1;
  for (let i = 0; i < targetLength; i += 1) {
    const sourcePos = (i / Math.max(1, targetLength - 1)) * maxIndex;
    const left = Math.floor(sourcePos);
    const right = Math.min(maxIndex, left + 1);
    const alpha = sourcePos - left;
    resized[i] = samples[left] * (1 - alpha) + samples[right] * alpha;
  }
  return resized;
}

function splitIntoWindows(samples: Float32Array): Float32Array[] {
  const window = ACOUSTIC_DEFAULTS.yamnetWindowSamples;
  const hop = ACOUSTIC_DEFAULTS.yamnetWindowHopSamples;
  if (samples.length <= window) return [resizeToWindow(samples, window)];
  const chunks: Float32Array[] = [];
  for (let start = 0; start + window <= samples.length; start += hop) {
    chunks.push(samples.slice(start, start + window));
  }
  if (!chunks.length) chunks.push(resizeToWindow(samples, window));
  return chunks;
}

function rowToArray(tensorData: Float32Array, rowLength: number, rowIndex: number): number[] {
  const offset = rowIndex * rowLength;
  const row: number[] = [];
  for (let i = 0; i < rowLength; i += 1) row.push(tensorData[offset + i] ?? 0);
  return row;
}

export class YamnetClassifier {
  private model: tf.GraphModel | null = null;

  private loadPromise: Promise<void> | null = null;

  private loadFailed = false;

  async ensureLoaded(): Promise<void> {
    if (this.model || this.loadFailed) return;
    if (!this.loadPromise) {
      this.loadPromise = (async () => {
        try {
          await tf.ready();
          this.model = await tf.loadGraphModel(YAMNET_MODEL_URL);
        } catch {
          this.loadFailed = true;
          this.model = null;
        }
      })().finally(() => {
        this.loadPromise = null;
      });
    }
    await this.loadPromise;
  }

  isLoaded(): boolean {
    return Boolean(this.model);
  }

  async classifyAudio(samples: Float32Array): Promise<GroupedYamnetScores> {
    if (!samples.length) return { ...EMPTY_GROUPED_SCORES };
    await this.ensureLoaded();
    if (!this.model) return { ...EMPTY_GROUPED_SCORES };

    const windows = splitIntoWindows(samples);
    const perWindowScores: GroupedYamnetScores[] = [];

    for (const windowSamples of windows) {
      const grouped = tf.tidy(() => {
        const input = tf.tensor1d(windowSamples);
        const output = this.model!.predict(input) as tf.Tensor | tf.Tensor[];
        const scoresTensor = Array.isArray(output) ? output[0] : output;
        const [frameCount, classCount] = scoresTensor.shape.length === 2 ? scoresTensor.shape : [1, scoresTensor.shape[0]];
        const values = Array.from(scoresTensor.dataSync());
        const frameGrouped: GroupedYamnetScores[] = [];
        for (let row = 0; row < frameCount; row += 1) {
          const rowScores = rowToArray(values as unknown as Float32Array, classCount, row);
          frameGrouped.push({
            speech: aggregateGroup(rowScores, YAMNET_GROUP_CLASS_INDICES.speech),
            conversation: aggregateGroup(rowScores, YAMNET_GROUP_CLASS_INDICES.conversation),
            babble: aggregateGroup(rowScores, YAMNET_GROUP_CLASS_INDICES.babble),
            shouting: aggregateGroup(rowScores, YAMNET_GROUP_CLASS_INDICES.shouting)
          });
        }

        return {
          speech: mean(frameGrouped.map((entry) => entry.speech)),
          conversation: mean(frameGrouped.map((entry) => entry.conversation)),
          babble: mean(frameGrouped.map((entry) => entry.babble)),
          shouting: mean(frameGrouped.map((entry) => entry.shouting))
        };
      });
      perWindowScores.push(grouped);
    }

    return {
      speech: Number(normalizeScore(mean(perWindowScores.map((entry) => entry.speech))).toFixed(4)),
      conversation: Number(normalizeScore(mean(perWindowScores.map((entry) => entry.conversation))).toFixed(4)),
      babble: Number(normalizeScore(mean(perWindowScores.map((entry) => entry.babble))).toFixed(4)),
      shouting: Number(normalizeScore(mean(perWindowScores.map((entry) => entry.shouting))).toFixed(4))
    };
  }
}

